import React, { useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BottomNav } from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import { Gem, Star, CheckCircle, Calendar, Circle, Plus, Check, Shield, Camera, X, QrCode, Upload, FileText, File, Info, Award, FileCheck, Clock, TrendingUp } from 'lucide-react';
interface ActivityPageProps {
  onNavigate: (page: string) => void;
  actionIdToOpen?: string | null;
  onConsumedActionIdToOpen?: () => void;
  defaultTab?: 'planned' | 'unplanned' | 'recent';
  previousPage?: string;
}
export function ActivityPage({
  onNavigate,
  actionIdToOpen,
  onConsumedActionIdToOpen,
  defaultTab = 'planned',
  previousPage = 'home'
}: ActivityPageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'planned' | 'unplanned' | 'recent'>(defaultTab);
  const [showCertificationModal, setShowCertificationModal] = useState(false);
  const [showSelfCertificationModal, setShowSelfCertificationModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [selectedAction, setSelectedAction] = useState<any | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isCreditsClosing, setIsCreditsClosing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFilePreview, setUploadedFilePreview] = useState<string | null>(null);
  const [certificationNotes, setCertificationNotes] = useState('');
  // Scheduling state for "Da Pianificare" tab
  const [schedulingActionId, setSchedulingActionId] = useState<string | null>(null);
  const [scheduledDates, setScheduledDates] = useState<Record<string, string>>({});
  const {
    wallet,
    actions,
    uploadProof,
    showToast
  } = useApp();
  // Auto-open modal if actionIdToOpen is provided
  useEffect(() => {
    if (actionIdToOpen) {
      const action = actions.find(a => a.id === actionIdToOpen);
      if (action) {
        setSelectedAction(action);
        if (action.type === 'diamond') {
          setShowCertificationModal(true);
        } else {
          setShowSelfCertificationModal(true);
        }
      }
      if (onConsumedActionIdToOpen) {
        onConsumedActionIdToOpen();
      }
    }
  }, [actionIdToOpen, actions, onConsumedActionIdToOpen]);
  useEffect(() => {
    const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
    const handleScroll = () => {
      if (scrollArea) {
        setIsScrolled(scrollArea.scrollTop > 20);
      }
    };
    if (scrollArea) {
      scrollArea.addEventListener('scroll', handleScroll);
      return () => scrollArea.removeEventListener('scroll', handleScroll);
    }
  }, []);
  const handleVerifyTask = (action: any) => {
    setSelectedAction(action);
    if (action.type === 'diamond') {
      setShowCertificationModal(true);
    } else {
      setShowSelfCertificationModal(true);
    }
  };
  const handleCloseCertificationModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowCertificationModal(false);
      setShowSelfCertificationModal(false);
      setSelectedAction(null);
      setIsClosing(false);
      setUploadedFile(null);
      setUploadedFilePreview(null);
      setCertificationNotes('');
    }, 300);
  };
  const handleCloseCreditsModal = () => {
    setIsCreditsClosing(true);
    setTimeout(() => {
      setShowCreditsModal(false);
      setIsCreditsClosing(false);
    }, 300);
  };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setUploadedFilePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setUploadedFilePreview(null);
      }
    }
  };
  const handleSubmitCertification = () => {
    if (!selectedAction || !uploadedFile) return;
    uploadProof(selectedAction.id, uploadedFile);
    showToast({
      variant: 'success',
      message: 'Prova caricata!'
    });
    // Navigate back to the page the user came from
    handleCloseCertificationModal();
    onNavigate(previousPage);
  };
  const handleConfirmSchedule = (actionId: string) => {
    const date = scheduledDates[actionId];
    if (!date) return;
    showToast({
      variant: 'success',
      message: 'Attività pianificata!'
    });
    setSchedulingActionId(null);
  };
  // Filter actions
  const plannedActions = actions.filter(a => a.status === 'planned' && a.scheduledAt);
  const unplannedActions = [{
    id: 'unplanned-1',
    emoji: '💡',
    title: 'Installare lampadine LED',
    points: 20,
    type: 'gold' as const,
    status: 'planned' as const
  }, {
    id: 'unplanned-2',
    emoji: '🚿',
    title: 'Fare docce brevi',
    points: 15,
    type: 'gold' as const,
    status: 'planned' as const
  }];
  const recentActions = [{
    id: 'recent-1',
    emoji: '🚲',
    title: 'In bici al lavoro',
    completedAt: 'Oggi, 8:30',
    points: 50,
    type: 'diamond' as const,
    status: 'completed' as const
  }, {
    id: 'recent-2',
    emoji: '♻️',
    title: 'Riciclato il vetro',
    completedAt: 'Ieri, 14:20',
    points: 10,
    type: 'gold' as const,
    status: 'completed' as const
  }];
  const todayPlanned = plannedActions.filter(a => a.scheduledAt?.startsWith('Oggi'));
  const upcomingPlanned = plannedActions.filter(a => !a.scheduledAt?.startsWith('Oggi'));
  const weeklyImpact = 85;
  // Today's date formatted for min attribute on date input
  const todayISO = new Date().toISOString().split('T')[0];
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-199">
      {/* Fixed Header */}
      <header className={`fixed top-0 left-0 right-0 px-6 py-4 flex justify-between items-center z-30 transition-all duration-300 ${isScrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'}`} data-id="element-200">
        <div className="flex items-center" data-id="element-201">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className={`h-8 w-auto transition-all duration-300 ${isScrolled ? '' : 'brightness-0 invert'}`} data-id="element-202" />
        </div>
        <div className="flex items-center gap-3" data-id="element-203">
          <button onClick={() => setShowCreditsModal(true)} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-green-600' : 'text-white'}`} data-id="element-204">
            <Gem className="h-5 w-5" strokeWidth={2} data-id="element-205" />
            <span className="text-sm font-normal" data-id="element-206">{wallet.diamonds}</span>
          </button>
          <button onClick={() => setShowCreditsModal(true)} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-gray-600' : 'text-white'}`} data-id="element-207">
            <Star className="h-5 w-5" strokeWidth={2} data-id="element-208" />
            <span className="text-sm font-normal" data-id="element-209">{wallet.stars}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <ScrollArea className="flex-1" data-id="element-210">
        {/* Hero */}
        <div className="bg-green-600 w-full relative pt-20 pb-10" data-id="element-211">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
          backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/wsCbUWp4TF3ni7DwEfb9qP/hand-drawn-turtle-shell-pattern_23-2150598661.jpg)'
        }} data-id="element-212" />
          <div className="relative z-10 px-6 max-w-md mx-auto" data-id="element-213">
            <div className="mb-5" data-id="element-214">
              <h2 className="font-normal text-2xl text-white mb-1" data-id="element-215">
                Le tue attività
              </h2>
              <p className="text-sm text-white/80" data-id="element-216">
                {new Date().toLocaleDateString('it-IT', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20" data-id="element-217">
              <div className="flex items-center justify-between mb-3" data-id="element-218">
                <div className="flex items-center gap-2" data-id="element-219">
                  <TrendingUp className="h-5 w-5 text-white" strokeWidth={2} data-id="element-220" />
                  <span className="text-sm text-white/90" data-id="element-221">
                    Impatto settimanale
                  </span>
                </div>
                <span className="text-2xl font-normal text-white" data-id="element-222">
                  {weeklyImpact}%
                </span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2" data-id="element-223">
                <div className="bg-white h-full rounded-full transition-all duration-500" style={{
                width: `${weeklyImpact}%`
              }} data-id="element-224" />
              </div>
              <p className="text-xs text-white/70 mt-2" data-id="element-225">
                {plannedActions.length} azioni pianificate •{' '}
                {recentActions.length} completate
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 pb-28 max-w-md mx-auto relative" data-id="element-226">
          {/* Tab Switcher */}
          <div className="relative -mt-3 mb-6" data-id="element-227">
            <div className="bg-white p-1 rounded-full border border-gray-200 flex overflow-x-auto scrollbar-hide" data-id="element-228">
              <button onClick={() => setActiveTab('planned')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 whitespace-nowrap ${activeTab === 'planned' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-229">
                Pianificate
              </button>
              <button onClick={() => setActiveTab('unplanned')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 whitespace-nowrap ${activeTab === 'unplanned' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-230">
                Da Pianificare
              </button>
              <button onClick={() => setActiveTab('recent')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 whitespace-nowrap ${activeTab === 'recent' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-231">
                Recenti
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6" data-id="element-232">
            {activeTab === 'planned' && <>
                {/* Legend */}
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-id="element-233">
                  <button onClick={() => setShowLegend(!showLegend)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors" data-id="element-234">
                    <div className="flex items-center gap-2" data-id="element-235">
                      <Info className="h-4 w-4 text-gray-400" strokeWidth={2} data-id="element-236" />
                      <span className="text-xs text-gray-500" data-id="element-237">
                        Come funzionano Certifica e Verifica?
                      </span>
                    </div>
                    <span className="text-xs text-green-600" data-id="element-238">
                      {showLegend ? 'Chiudi' : 'Scopri'}
                    </span>
                  </button>
                  {showLegend && <div className="px-4 pb-4 space-y-2.5 border-t border-gray-100" data-id="element-239">
                      <div className="flex items-start gap-3 pt-3" data-id="element-240">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 border-green-600 text-green-600 flex-shrink-0" data-id="element-241">
                          <Shield className="h-3 w-3" strokeWidth={2} data-id="element-242" />
                          <span className="text-xs font-normal" data-id="element-243">Certifica</span>
                          <Gem className="h-3 w-3" strokeWidth={2} data-id="element-244" />
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed pt-0.5" data-id="element-245">
                          Verifica ufficiale con documento o foto. L'azione
                          viene registrata su <strong data-id="element-246">Blockchain</strong> e
                          guadagni <strong data-id="element-247">Punti Diamond</strong> (valore
                          monetario).
                        </p>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-248">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 border-green-600 text-green-600 flex-shrink-0" data-id="element-249">
                          <span className="text-xs font-normal" data-id="element-250">Verifica</span>
                          <Star className="h-3 w-3" strokeWidth={2} data-id="element-251" />
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed pt-0.5" data-id="element-252">
                          Auto-certificazione con foto opzionale. Guadagni{' '}
                          <strong data-id="element-253">Punti Stelle</strong> immediatamente (valore
                          non monetario).
                        </p>
                      </div>
                    </div>}
                </div>

                {todayPlanned.length > 0 && <div data-id="element-254">
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3 px-1" data-id="element-255">
                      Oggi
                    </h3>
                    <div className="space-y-3" data-id="element-256">
                      {todayPlanned.map(action => <div key={action.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-green-500 transition-colors bg-white" data-id="element-257">
                          <div className="text-2xl" data-id="element-258">{action.emoji}</div>
                          <div className="flex-1" data-id="element-259">
                            <p className="font-normal text-sm text-gray-900" data-id="element-260">
                              {action.title}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-261">
                              📅 {action.scheduledAt}
                            </p>
                          </div>
                          <button onClick={() => handleVerifyTask(action)} className="px-3 py-1.5 rounded-full text-xs font-normal transition-all bg-white border-2 border-green-600 text-green-600 hover:bg-green-50" data-id="element-262">
                            <span className="flex items-center gap-1" data-id="element-263">
                              {action.type === 'diamond' ? 'Certifica' : 'Verifica'}
                              {action.type === 'diamond' ? <Gem className="h-3 w-3" strokeWidth={2} data-id="element-264" /> : <Star className="h-3 w-3" strokeWidth={2} data-id="element-265" />}
                              <span data-id="element-266">+{action.points}</span>
                            </span>
                          </button>
                        </div>)}
                    </div>
                  </div>}

                {upcomingPlanned.length > 0 && <div data-id="element-267">
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3 px-1" data-id="element-268">
                      Prossime
                    </h3>
                    <div className="space-y-3" data-id="element-269">
                      {upcomingPlanned.map(action => <div key={action.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-green-500 transition-colors bg-white" data-id="element-270">
                          <div className="text-2xl" data-id="element-271">{action.emoji}</div>
                          <div className="flex-1" data-id="element-272">
                            <p className="font-normal text-sm text-gray-900" data-id="element-273">
                              {action.title}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-274">
                              📅 {action.scheduledAt}
                            </p>
                          </div>
                          <button onClick={() => handleVerifyTask(action)} className="px-3 py-1.5 rounded-full text-xs font-normal transition-all bg-white border-2 border-green-600 text-green-600 hover:bg-green-50" data-id="element-275">
                            <span className="flex items-center gap-1" data-id="element-276">
                              {action.type === 'diamond' ? 'Certifica' : 'Verifica'}
                              {action.type === 'diamond' ? <Gem className="h-3 w-3" strokeWidth={2} data-id="element-277" /> : <Star className="h-3 w-3" strokeWidth={2} data-id="element-278" />}
                              <span data-id="element-279">+{action.points}</span>
                            </span>
                          </button>
                        </div>)}
                    </div>
                  </div>}

                {plannedActions.length === 0 && <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200" data-id="element-280">
                    <div className="text-5xl mb-4" data-id="element-281">📅</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-282">
                      Nessuna attività pianificata
                    </h3>
                    <p className="text-sm text-gray-500 mb-6" data-id="element-283">
                      Pianifica le tue azioni per iniziare
                    </p>
                    <button onClick={() => onNavigate('impact')} className="px-6 py-2.5 rounded-xl text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-colors" data-id="element-284">
                      Esplora azioni
                    </button>
                  </div>}
              </>}

            {activeTab === 'unplanned' && <>
                {unplannedActions.length > 0 ? <div className="space-y-3" data-id="element-285">
                    {unplannedActions.map(action => <div key={action.id} className="rounded-xl border border-gray-100 bg-white overflow-hidden transition-all hover:border-green-200" data-id="element-286">
                        {/* Main row */}
                        <div className="flex items-center gap-3 p-3" data-id="element-287">
                          <div className="text-2xl" data-id="element-288">{action.emoji}</div>
                          <div className="flex-1" data-id="element-289">
                            <p className="font-normal text-sm text-gray-900" data-id="element-290">
                              {action.title}
                            </p>
                            {scheduledDates[action.id] ? <p className="text-xs text-green-600" data-id="element-291">
                                📅{' '}
                                {new Date(scheduledDates[action.id]).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                              </p> : <p className="text-xs text-gray-400" data-id="element-292">
                                Nessuna data impostata
                              </p>}
                          </div>
                          <button onClick={() => setSchedulingActionId(schedulingActionId === action.id ? null : action.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-normal transition-all border-2 ${schedulingActionId === action.id ? 'bg-gray-100 border-gray-200 text-gray-600' : 'bg-white border-green-600 text-green-600 hover:bg-green-50'}`} data-id="element-293">
                            <Calendar className="h-3 w-3" strokeWidth={2} data-id="element-294" />
                            {schedulingActionId === action.id ? 'Annulla' : 'Pianifica'}
                          </button>
                        </div>

                        {/* Inline date picker */}
                        {schedulingActionId === action.id && <div className="px-4 pb-4 pt-1 border-t border-gray-100 bg-gray-50" data-id="element-295">
                            <p className="text-xs text-gray-500 mb-2" data-id="element-296">
                              Scegli una data per questa attività:
                            </p>
                            <div className="flex items-center gap-2" data-id="element-297">
                              <input type="date" min={todayISO} value={scheduledDates[action.id] || ''} onChange={e => setScheduledDates(prev => ({
                      ...prev,
                      [action.id]: e.target.value
                    }))} className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors bg-white" data-id="element-298" />
                              <button onClick={() => handleConfirmSchedule(action.id)} disabled={!scheduledDates[action.id]} className="px-4 py-2 rounded-xl text-xs font-normal text-white bg-green-600 hover:bg-green-700 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed" data-id="element-299">
                                Conferma
                              </button>
                            </div>
                          </div>}
                      </div>)}
                  </div> : <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200" data-id="element-300">
                    <div className="text-5xl mb-4" data-id="element-301">✅</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-302">
                      Tutte le azioni sono pianificate
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-303">
                      Ottimo lavoro! Continua così
                    </p>
                  </div>}
              </>}

            {activeTab === 'recent' && <>
                {recentActions.length > 0 ? <div className="space-y-3" data-id="element-304">
                    {recentActions.map(action => <div key={action.id} className="bg-white rounded-xl p-4 border border-gray-100" data-id="element-305">
                        <div className="flex items-start gap-3 mb-3" data-id="element-306">
                          <div className="text-2xl" data-id="element-307">{action.emoji}</div>
                          <div className="flex-1" data-id="element-308">
                            <p className="font-normal text-sm text-gray-900 mb-1" data-id="element-309">
                              {action.title}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-310">
                              {action.completedAt}
                            </p>
                          </div>
                          <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full" data-id="element-311">
                            <Check className="h-3 w-3" data-id="element-312" />
                            Verificato
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-313">
                          <div className="flex items-center gap-1 text-sm font-normal text-gray-600" data-id="element-314">
                            {action.type === 'diamond' ? <Gem className="h-4 w-4 text-green-600" strokeWidth={2} data-id="element-315" /> : <Star className="h-4 w-4 text-gray-400" strokeWidth={2} data-id="element-316" />}
                            <span data-id="element-317">+{action.points}</span>
                          </div>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200" data-id="element-318">
                    <div className="text-5xl mb-4" data-id="element-319">🎯</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-320">
                      Nessuna attività completata
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-321">
                      Completa la tua prima azione per vederla qui
                    </p>
                  </div>}
              </>}
          </div>
        </div>
      </ScrollArea>

      <BottomNav activePage="activity" onNavigate={onNavigate} data-id="element-322" />

      {/* Credits Modal */}
      {showCreditsModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseCreditsModal} data-id="element-323" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isCreditsClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-324">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-325">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-326" />
              <div className="flex items-center justify-between" data-id="element-327">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-328">
                  Come funzionano i crediti
                </h3>
                <button onClick={handleCloseCreditsModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-329">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-330" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-331">
              <div className="space-y-6" data-id="element-332">
                <div data-id="element-333">
                  <h4 className="font-normal text-base text-gray-900 mb-4" data-id="element-334">
                    Livelli di verifica delle azioni
                  </h4>
                  <div className="space-y-3" data-id="element-335">
                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-336">
                      <div className="flex items-start gap-3" data-id="element-337">
                        <FileCheck className="h-5 w-5 text-gray-400 flex-shrink-0" strokeWidth={2} data-id="element-338" />
                        <div className="flex-1" data-id="element-339">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-340">
                            Non verificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-341">
                            Azioni tracciate per statistiche e profilazione. Non
                            danno crediti.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-342">
                      <div className="flex items-start gap-3" data-id="element-343">
                        <Award className="h-5 w-5 text-yellow-500 flex-shrink-0" strokeWidth={2} data-id="element-344" />
                        <div className="flex-1" data-id="element-345">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-346">
                            Verificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-347">
                            Verifica tramite auto-certificazione. Danno crediti
                            Stelle.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-348">
                      <div className="flex items-start gap-3" data-id="element-349">
                        <Shield className="h-5 w-5 text-green-600 flex-shrink-0" strokeWidth={2} data-id="element-350" />
                        <div className="flex-1" data-id="element-351">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-352">
                            Certificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-353">
                            Certificate da Greenback o partner ufficiali.
                            Registrate su Blockchain. Danno crediti Diamond.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-354">
                  <h4 className="font-normal text-base text-gray-900 mb-4" data-id="element-355">
                    Cosa puoi fare con i crediti
                  </h4>
                  <div className="space-y-3" data-id="element-356">
                    <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-357">
                      <div className="flex items-start gap-3 mb-3" data-id="element-358">
                        <Star className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={2} fill="currentColor" data-id="element-359" />
                        <div className="flex-1" data-id="element-360">
                          <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-361">
                            Crediti Stelle
                          </h4>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-362">
                            Valore non monetario
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2" data-id="element-363">
                        <p className="text-xs text-gray-700" data-id="element-364">
                          <span className="font-medium" data-id="element-365">✓</span> Partecipazione
                          a eventi esclusivi
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-366">
                          <span className="font-medium" data-id="element-367">✓</span> Sblocco di
                          feature premium nell'app
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-368">
                          <span className="font-medium" data-id="element-369">✓</span> Accesso a
                          contenuti riservati
                        </p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-370">
                      <div className="flex items-start gap-3 mb-3" data-id="element-371">
                        <Gem className="h-6 w-6 text-green-600 flex-shrink-0" strokeWidth={2} data-id="element-372" />
                        <div className="flex-1" data-id="element-373">
                          <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-374">
                            Crediti Diamond
                          </h4>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-375">
                            Valore monetario e ufficiale
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2" data-id="element-376">
                        <p className="text-xs text-gray-700" data-id="element-377">
                          <span className="font-medium" data-id="element-378">✓</span> Convertibili in
                          buoni acquisto
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-379">
                          <span className="font-medium" data-id="element-380">✓</span> Convertibili in
                          denaro
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-381">
                          <span className="font-medium" data-id="element-382">✓</span> Certificazione
                          su Blockchain
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-383">
                          <span className="font-medium" data-id="element-384">✓</span> Riconoscimento
                          ufficiale e formale
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-385">
                  <div className="flex items-start gap-3" data-id="element-386">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} data-id="element-387" />
                    <p className="text-xs text-blue-900 leading-relaxed" data-id="element-388">
                      <span className="font-medium" data-id="element-389">Nota:</span> Le azioni
                      certificate richiedono una verifica ufficiale da parte di
                      Greenback o dei nostri partner autorizzati. Una volta
                      certificate, vengono registrate permanentemente su
                      Blockchain per garantire trasparenza e autenticità.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Certification Modal (Diamond) */}
      {showCertificationModal && selectedAction && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseCertificationModal} data-id="element-390" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-391">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-392">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-393" />
              <div className="flex items-center justify-between" data-id="element-394">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-395">
                  Certificazione ufficiale
                </h3>
                <button onClick={handleCloseCertificationModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-396">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-397" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-398">
              <div className="space-y-6" data-id="element-399">
                <div className="bg-green-50 rounded-xl p-4 border border-green-100" data-id="element-400">
                  <div className="flex items-center gap-3 mb-2" data-id="element-401">
                    <div className="text-3xl" data-id="element-402">{selectedAction.emoji}</div>
                    <div className="flex-1" data-id="element-403">
                      <h4 className="font-normal text-base text-gray-900" data-id="element-404">
                        {selectedAction.title}
                      </h4>
                      <div className="flex items-center gap-1 text-xs text-green-600 mt-1" data-id="element-405">
                        <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-406" />
                        <span data-id="element-407">+{selectedAction.points} Punti Diamond</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-408">
                  <label className="block text-sm font-normal text-gray-900 mb-3" data-id="element-409">
                    Carica documento o foto
                  </label>
                  {uploadedFile ? <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-410">
                      <div className="flex items-start gap-3" data-id="element-411">
                        {uploadedFilePreview ? <img src={uploadedFilePreview} alt="Preview" className="w-20 h-20 object-cover rounded-lg" data-id="element-412" /> : <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-413">
                            <File className="h-8 w-8 text-gray-400" data-id="element-414" />
                          </div>}
                        <div className="flex-1 min-w-0" data-id="element-415">
                          <p className="text-sm font-normal text-gray-900 truncate" data-id="element-416">
                            {uploadedFile.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-417">
                            {(uploadedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <button onClick={() => {
                    setUploadedFile(null);
                    setUploadedFilePreview(null);
                  }} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-418">
                          <X className="h-5 w-5" data-id="element-419" />
                        </button>
                      </div>
                    </div> : <label className="w-full bg-gray-50 rounded-xl p-8 border-2 border-dashed border-gray-300 hover:border-green-500 transition-all cursor-pointer flex flex-col items-center justify-center" data-id="element-420">
                      <Upload className="h-12 w-12 text-gray-400 mb-3" data-id="element-421" />
                      <p className="text-sm font-normal text-gray-600 mb-1" data-id="element-422">
                        Carica file
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-423">
                        PDF, DOC, DOCX, JPG, PNG
                      </p>
                      <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" data-id="element-424" />
                    </label>}
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-425">
                  <div className="flex items-start gap-3" data-id="element-426">
                    <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-427" />
                    <p className="text-xs text-blue-900 leading-relaxed" data-id="element-428">
                      Le azioni certificate vengono verificate ufficialmente e
                      registrate su Blockchain. Riceverai i tuoi Punti Diamond
                      dopo la conferma.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white" data-id="element-429">
              <button onClick={handleSubmitCertification} disabled={!uploadedFile} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-430">
                Carica prova
              </button>
            </div>
          </div>
        </>}

      {/* Self-Certification Modal (Star) */}
      {showSelfCertificationModal && selectedAction && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseCertificationModal} data-id="element-431" />
          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-432">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-433">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-434" />
              <div className="flex items-center justify-between" data-id="element-435">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-436">
                  Auto-certificazione
                </h3>
                <button onClick={handleCloseCertificationModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-437">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-438" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-439">
              <div className="space-y-6" data-id="element-440">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-441">
                  <div className="flex items-center gap-3 mb-2" data-id="element-442">
                    <div className="text-3xl" data-id="element-443">{selectedAction.emoji}</div>
                    <div className="flex-1" data-id="element-444">
                      <h4 className="font-normal text-base text-gray-900" data-id="element-445">
                        {selectedAction.title}
                      </h4>
                      <div className="flex items-center gap-1 text-xs text-gray-600 mt-1" data-id="element-446">
                        <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-447" />
                        <span data-id="element-448">+{selectedAction.points} Punti Stelle</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-449">
                  <label className="block text-sm font-normal text-gray-900 mb-3" data-id="element-450">
                    Aggiungi una foto (opzionale)
                  </label>
                  {uploadedFile ? <div className="relative" data-id="element-451">
                      <img src={uploadedFilePreview} alt="Uploaded" className="w-full h-48 object-cover rounded-xl border border-gray-200" data-id="element-452" />
                      <button onClick={() => setUploadedFile(null)} className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors" data-id="element-453">
                        <X className="h-4 w-4 text-gray-600" data-id="element-454" />
                      </button>
                    </div> : <label className="w-full bg-gray-50 rounded-xl p-8 border-2 border-dashed border-gray-300 hover:border-green-500 transition-all cursor-pointer flex flex-col items-center justify-center" data-id="element-455">
                      <Camera className="h-12 w-12 text-gray-400 mb-3" data-id="element-456" />
                      <p className="text-sm font-normal text-gray-600 mb-1" data-id="element-457">
                        Carica una foto
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-458">
                        Documenta la tua azione
                      </p>
                      <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" data-id="element-459" />
                    </label>}
                </div>
                <div data-id="element-460">
                  <label className="block text-sm font-normal text-gray-900 mb-3" data-id="element-461">
                    Note aggiuntive (opzionale)
                  </label>
                  <textarea value={certificationNotes} onChange={e => setCertificationNotes(e.target.value)} placeholder="Descrivi come hai completato questa azione..." rows={4} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-green-500 transition-colors resize-none" data-id="element-462" />
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200" data-id="element-463">
                  <div className="flex items-start gap-3" data-id="element-464">
                    <FileText className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-465" />
                    <p className="text-xs text-yellow-900 leading-relaxed" data-id="element-466">
                      L'auto-certificazione ti permette di confermare il
                      completamento dell'azione. I Punti Stelle verranno
                      aggiunti immediatamente al tuo saldo.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white" data-id="element-467">
              <button onClick={handleSubmitCertification} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-468">
                Conferma completamento
              </button>
            </div>
          </div>
        </>}
    </div>;
}